import { Select } from '@/components/forms/Select';
import { LANGUAGES, type City, type Language } from '@pomoshnik/shared';

/**
 * Pure presenter Stage 1 онбординга (`/onboarding/lang-city`, PRD §2.2).
 *
 * Stage 1 — простой выбор языка интерфейса + города. После submit Container
 * сохраняет в `authStore.setLangCity(...)` (region выводится автоматически
 * через `CITY_REGION_MAP`) и редиректит на Главную (`/`).
 *
 * Layout сверху вниз:
 *   - HomeHeader-аналога нет — на этом экране ещё нет language-switcher'а
 *     (lang выбирается прямо здесь). Простая шапка с логотипом без `<header>`.
 *   - H1 «Добро пожаловать»  — placeholder текст (PRD §2.2 не диктует точный
 *     текст для Stage 1; в Phase 2 hardcoded на ru).
 *   - Select язык / Select город — вертикальный layout, P-019.
 *   - Submit-button — vertical stack под селектами, P-019.
 *
 * Без BottomBar (P-023): экран онбординга — entry-point, navigation back/home
 * не имеет смысла.
 */
export interface LangCityScreenViewProps {
  cityOptions: ReadonlyArray<{ value: City; label: string }>;
  selectedLang: Language | '';
  selectedCity: City | '';
  onLangChange: (lang: Language) => void;
  onCityChange: (city: City) => void;
  onSubmit: () => void;
  /** Сообщение если выбраны не все поля. */
  validationError: string | null;
  /** Disabled во время загрузки/после submit. */
  submitting: boolean;
}

const LANG_LABELS: Record<Language, string> = {
  ru: 'Русский',
  kz: 'Қазақша',
  uz: "O'zbekcha",
};

export function LangCityScreenView({
  cityOptions,
  selectedLang,
  selectedCity,
  onLangChange,
  onCityChange,
  onSubmit,
  validationError,
  submitting,
}: LangCityScreenViewProps) {
  const langOptions = LANGUAGES.map((l) => ({ value: l, label: LANG_LABELS[l] }));

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <div className="h-16 flex items-center px-4 bg-header border-b border-header-border">
        <span
          className="font-display text-h-logo text-ink"
          style={{ letterSpacing: '0.02em', lineHeight: 1 }}
        >
          Помощник мебельщика
        </span>
      </div>

      <section className="px-4 py-6 flex flex-col gap-5">
        <h1 className="font-display text-h1 text-ink">Добро пожаловать</h1>

        <Select
          name="lang"
          label="Язык интерфейса"
          value={selectedLang}
          onChange={(v) => onLangChange(v as Language)}
          options={langOptions}
          placeholder="Выберите язык"
          required
          disabled={submitting}
        />

        <Select
          name="city"
          label="Ваш город"
          value={selectedCity}
          onChange={(v) => onCityChange(v as City)}
          options={cityOptions}
          placeholder="Выберите город"
          required
          disabled={submitting}
        />

        {validationError !== null ? (
          <p role="alert" className="text-error text-sm">
            {validationError}
          </p>
        ) : null}

        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className={
            'min-h-tap rounded-xl bg-cta text-canvas text-base font-semibold px-4 py-3 ' +
            'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ' +
            'disabled:opacity-60 disabled:cursor-not-allowed'
          }
        >
          {submitting ? 'Сохраняем…' : 'Продолжить'}
        </button>
      </section>
    </main>
  );
}
